import React from 'react';

const C = {
  title: '#1e293b',
  titleWhite: '#ffffff',
  flow: '#3B82F6',
  tagline: '#64748b',
  taglineLight: '#94a3b8',
};

/* ------------------------------------------------------------------ */
/*  FULL — with tagline "A PNA Title Company"                         */
/*  Usage: Login page header                                          */
/* ------------------------------------------------------------------ */
const Full = (props) => (
  <svg
    viewBox="0 0 340 108"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="TitleFlow — A PNA Title Company"
    {...props}
  >
    <text
      x="10" y="58"
      fontFamily="'Oswald', sans-serif"
      fontWeight="700"
      fontSize="54"
      fill={C.title}
      letterSpacing="1"
    >
      TITLE
    </text>
    <text
      x="148" y="60"
      fontFamily="'Dancing Script', cursive"
      fontWeight="700"
      fontSize="52"
      fill={C.flow}
    >
      Flow
    </text>
    <path
      d="M 16 72 Q 160 90, 322 50"
      stroke={C.flow}
      strokeWidth="2.6"
      fill="none"
      strokeLinecap="round"
      opacity="0.7"
    />
    <text
      x="170" y="98"
      fontFamily="'Inter', 'Helvetica Neue', sans-serif"
      fontWeight="400"
      fontSize="14"
      fill={C.tagline}
      textAnchor="middle"
      letterSpacing="1.2"
    >
      A PNA Title Company
    </text>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  COMPACT — no tagline, optimised for small header display          */
/*  Usage: Dashboard top-left header                                  */
/* ------------------------------------------------------------------ */
const Compact = (props) => (
  <svg
    viewBox="0 0 248 48"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="TitleFlow"
    {...props}
  >
    <text
      x="4" y="33"
      fontFamily="'Oswald', sans-serif"
      fontWeight="700"
      fontSize="37"
      fill={C.title}
      letterSpacing="0.5"
    >
      TITLE
    </text>
    <text
      x="100" y="35"
      fontFamily="'Dancing Script', cursive"
      fontWeight="700"
      fontSize="35"
      fill={C.flow}
    >
      Flow
    </text>
    <path
      d="M 10 41 Q 115 50, 236 26"
      stroke={C.flow}
      strokeWidth="1.8"
      fill="none"
      strokeLinecap="round"
      opacity="0.6"
    />
  </svg>
);

/* ------------------------------------------------------------------ */
/*  DARK — white TITLE, blue Flow, for dark backgrounds               */
/* ------------------------------------------------------------------ */
const Dark = (props) => (
  <svg
    viewBox="0 0 340 108"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="TitleFlow — A PNA Title Company"
    {...props}
  >
    <text
      x="10" y="58"
      fontFamily="'Oswald', sans-serif"
      fontWeight="700"
      fontSize="54"
      fill={C.titleWhite}
      letterSpacing="1"
    >
      TITLE
    </text>
    <text
      x="148" y="60"
      fontFamily="'Dancing Script', cursive"
      fontWeight="700"
      fontSize="52"
      fill={C.flow}
    >
      Flow
    </text>
    <path
      d="M 16 72 Q 160 90, 322 50"
      stroke={C.flow}
      strokeWidth="2.6"
      fill="none"
      strokeLinecap="round"
      opacity="0.7"
    />
    <text
      x="170" y="98"
      fontFamily="'Inter', 'Helvetica Neue', sans-serif"
      fontWeight="400"
      fontSize="14"
      fill={C.taglineLight}
      textAnchor="middle"
      letterSpacing="1.2"
    >
      A PNA Title Company
    </text>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  DARK LIGHT — white TITLE, bright Flow, bright tagline for dark bg */
/*  Usage: Login page on dark backgrounds                              */
/* ------------------------------------------------------------------ */
const DarkBright = (props) => (
  <svg
    viewBox="0 0 340 108"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    role="img"
    aria-label="TitleFlow — A PNA Title Company"
    {...props}
  >
    <defs>
      <filter id="textGlow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="5" floodColor="#000000" floodOpacity="0.6" />
      </filter>
      <filter id="subtitleGlow" x="-20%" y="-20%" width="140%" height="140%">
        <feDropShadow dx="0" dy="2" stdDeviation="4" floodColor="#000000" floodOpacity="0.5" />
      </filter>
    </defs>
    <text
      x="10" y="58"
      fontFamily="'Oswald', sans-serif"
      fontWeight="700"
      fontSize="54"
      fill="#ffffff"
      letterSpacing="1"
      filter="url(#textGlow)"
    >
      TITLE
    </text>
    <text
      x="148" y="60"
      fontFamily="'Dancing Script', cursive"
      fontWeight="700"
      fontSize="52"
      fill="#60A5FA"
      filter="url(#textGlow)"
    >
      Flow
    </text>
    <path
      d="M 16 72 Q 160 90, 322 50"
      stroke="#60A5FA"
      strokeWidth="2.6"
      fill="none"
      strokeLinecap="round"
      opacity="0.8"
    />
    <text
      x="170" y="98"
      fontFamily="'Inter', 'Helvetica Neue', sans-serif"
      fontWeight="500"
      fontSize="14"
      fill="#CBD5E1"
      textAnchor="middle"
      letterSpacing="1.2"
      filter="url(#subtitleGlow)"
    >
      A PNA Title Company
    </text>
  </svg>
);

/* ------------------------------------------------------------------ */
/*  Public API                                                        */
/* ------------------------------------------------------------------ */
const variants = { full: Full, compact: Compact, dark: Dark, darkBright: DarkBright };

const TitleFlowLogo = ({ variant = 'compact', className = '', style = {}, onClick, ...rest }) => {
  const Comp = variants[variant] || Compact;
  return <Comp className={className} style={{ display: 'block', ...style }} onClick={onClick} {...rest} />;
};

export { Full, Compact, Dark, DarkBright };
export default TitleFlowLogo;
